
import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle,
  Handshake,
  ArrowRight,
  DollarSign,
  RefreshCw,
  Truck,
  ShieldCheck,
  Zap,
  AlertCircle,
  Check,
  X
} from "lucide-react";
import { SupplierScoreCard } from "@/components/supplier/SupplierScoreCard";
import { NegotiationOpportunity } from "@/components/supplier/NegotiationOpportunity";
import { PricingAlert } from "@/components/supplier/PricingAlert";
import { PartnershipRecommendation } from "@/components/supplier/PartnershipRecommendation";
import { SwitchingCostCalculator } from "@/components/supplier/SwitchingCostCalculator";

// Mock data
const suppliers = [
  {
    id: 1,
    name: "Finca Agrícola Canaria",
    category: "Produce",
    priceScore: 85,
    reliabilityScore: 90,
    deliveryScore: 78,
    overallScore: 84,
    priceChange: 5,
    lastOrderDate: "2023-05-15",
    aboveMarketRate: true,
    competitorsHaveBetterRates: true,
    volumeDiscountEligible: true
  },
  {
    id: 2,
    name: "Carnicería Atlántica",
    category: "Meat",
    priceScore: 68,
    reliabilityScore: 95,
    deliveryScore: 89,
    overallScore: 78,
    priceChange: -8,
    lastOrderDate: "2023-05-18",
    aboveMarketRate: true,
    competitorsHaveBetterRates: false,
    volumeDiscountEligible: true
  },
  {
    id: 3,
    name: "Pescados Isleños",
    category: "Seafood",
    priceScore: 92,
    reliabilityScore: 82,
    deliveryScore: 90,
    overallScore: 88,
    priceChange: 2,
    lastOrderDate: "2023-05-12",
    aboveMarketRate: false,
    competitorsHaveBetterRates: false,
    volumeDiscountEligible: false
  },
  {
    id: 4,
    name: "Lácteos Guanche",
    category: "Dairy",
    priceScore: 75,
    reliabilityScore: 88,
    deliveryScore: 92,
    overallScore: 82,
    priceChange: -3,
    lastOrderDate: "2023-05-20",
    aboveMarketRate: false,
    competitorsHaveBetterRates: true,
    volumeDiscountEligible: true
  },
  {
    id: 5,
    name: "Importaciones El Teide",
    category: "Specialty",
    priceScore: 62,
    reliabilityScore: 79,
    deliveryScore: 72,
    overallScore: 69,
    priceChange: 9,
    lastOrderDate: "2023-05-10",
    aboveMarketRate: true,
    competitorsHaveBetterRates: true,
    volumeDiscountEligible: false
  }
];

const negotiationOpportunities = [
  {
    id: 1,
    supplier: "Finca Agrícola Canaria",
    category: "Produce",
    product: "Organic Tomatoes",
    currentPrice: 3.98,
    marketRate: 3.15,
    percentAbove: 26.3,
    potentialSavings: 498.00,
    volumeHistory: "Consistent"
  },
  {
    id: 2,
    supplier: "Carnicería Atlántica",
    category: "Meat",
    product: "Prime Beef",
    currentPrice: 12.65,
    marketRate: 11.24,
    percentAbove: 12.5,
    potentialSavings: 845.00,
    volumeHistory: "Increasing"
  },
  {
    id: 3,
    supplier: "Importaciones El Teide",
    category: "Specialty",
    product: "Saffron",
    currentPrice: 85.45,
    marketRate: 72.99,
    percentAbove: 17.1,
    potentialSavings: 374.00,
    volumeHistory: "Seasonal"
  }
];

const pricingAlerts = [
  {
    id: 1,
    supplier: "Finca Agrícola Canaria",
    product: "Organic Spinach",
    yourPrice: 2.45,
    competitorPrice: 1.98,
    priceDiff: 23.7,
    competitor: "Restaurante El Faro",
    actionTaken: false
  },
  {
    id: 2,
    supplier: "Lácteos Guanche",
    product: "Artisanal Cheese",
    yourPrice: 9.87,
    competitorPrice: 8.50,
    priceDiff: 16.1,
    competitor: "Tapas La Palma",
    actionTaken: true
  }
];

const partnershipRecommendations = [
  {
    id: 1,
    supplier: "Finca Agrícola Canaria",
    category: "Produce",
    currentSpend: 2540.00,
    requiredForTier: 3000.00,
    potentialSavings: 382.00,
    savingsPercent: 15,
    additionalBenefits: ["Priority delivery", "Seasonal promotions"]
  },
  {
    id: 2,
    supplier: "Carnicería Atlántica",
    category: "Meat",
    currentSpend: 4850.00,
    requiredForTier: 5000.00,
    potentialSavings: 485.00,
    savingsPercent: 10,
    additionalBenefits: ["Custom cuts", "Storage service"]
  },
  {
    id: 3,
    supplier: "Pescados Isleños",
    category: "Seafood",
    currentSpend: 2100.00,
    requiredForTier: 3500.00,
    potentialSavings: 525.00,
    savingsPercent: 15,
    additionalBenefits: ["Special orders", "Flexible delivery"]
  }
];

const switchingOptions = [
  {
    id: 1,
    currentSupplier: "Finca Agrícola Canaria",
    newSupplier: "Huertas Tenerife",
    category: "Produce",
    currentCost: 2540.00,
    newCost: 2220.00,
    switchingFees: 150.00,
    contractTerms: "3 months",
    qualityDifference: "Similar",
    reliabilityRisk: "Low",
    logisticsChange: "Different delivery days"
  },
  {
    id: 2,
    currentSupplier: "Importaciones El Teide",
    newSupplier: "Sabores del Mundo",
    category: "Specialty",
    currentCost: 1850.00,
    newCost: 1650.00,
    switchingFees: 100.00,
    contractTerms: "None",
    qualityDifference: "Slightly lower",
    reliabilityRisk: "Medium",
    logisticsChange: "Longer lead times"
  }
];

const SupplierRelationshipOptimizer = () => {
  const [activeTab, setActiveTab] = useState("scores");
  const [refreshing, setRefreshing] = useState(false);
  const { toast } = useToast();

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => {
      setRefreshing(false);
      toast({
        title: "Data Refreshed",
        description: "Supplier data has been updated with the latest information.",
        duration: 3000,
      });
    }, 1500);
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-semibold tracking-tight">Supplier Relationship Optimizer</h1>
        <Button onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              Refreshing...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh Data
            </>
          )}
        </Button>
      </div>

      <Tabs defaultValue="scores" onValueChange={setActiveTab} value={activeTab}>
        <TabsList className="grid grid-cols-5 w-full">
          <TabsTrigger value="scores">
            Supplier Scores
          </TabsTrigger>
          <TabsTrigger value="negotiate">
            Negotiation Opportunities
          </TabsTrigger>
          <TabsTrigger value="alerts">
            Pricing Alerts
          </TabsTrigger>
          <TabsTrigger value="partnerships">
            Strategic Partnerships
          </TabsTrigger>
          <TabsTrigger value="switching">
            Switching Analysis
          </TabsTrigger>
        </TabsList>

        <TabsContent value="scores" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Supplier Performance Scores</CardTitle>
              <CardDescription>
                Review price competitiveness, reliability, and delivery speed for all suppliers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {suppliers.map(supplier => (
                  <SupplierScoreCard key={supplier.id} supplier={supplier} />
                ))}
              </div>
              
              <div className="mt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Price Score</TableHead>
                      <TableHead>Reliability</TableHead>
                      <TableHead>Delivery</TableHead>
                      <TableHead className="text-right">Overall</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {suppliers.map((supplier) => (
                      <TableRow key={supplier.id}>
                        <TableCell className="font-medium">{supplier.name}</TableCell>
                        <TableCell>{supplier.category}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={supplier.priceScore} className="w-16 h-2" />
                            <span>{supplier.priceScore}</span>
                            {supplier.priceChange !== 0 && (
                              supplier.priceChange > 0 ? (
                                <Badge variant="destructive" className="flex items-center">
                                  <TrendingUp className="h-3 w-3 mr-1" />
                                  {supplier.priceChange}%
                                </Badge>
                              ) : (
                                <Badge variant="default" className="flex items-center bg-green-600">
                                  <TrendingDown className="h-3 w-3 mr-1" />
                                  {Math.abs(supplier.priceChange)}%
                                </Badge>
                              )
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={supplier.reliabilityScore} className="w-16 h-2" />
                            <span>{supplier.reliabilityScore}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={supplier.deliveryScore} className="w-16 h-2" />
                            <span>{supplier.deliveryScore}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant={supplier.overallScore >= 80 ? "default" : supplier.overallScore >= 70 ? "secondary" : "destructive"}>
                            {supplier.overallScore}/100
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="negotiate" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Negotiation Opportunities</CardTitle>
              <CardDescription>
                Identify suppliers charging above-market rates for potential price negotiations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
                {negotiationOpportunities.map(opportunity => (
                  <NegotiationOpportunity key={opportunity.id} opportunity={opportunity} />
                ))}
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Current Price</TableHead>
                    <TableHead>Market Rate</TableHead>
                    <TableHead>Above Market</TableHead>
                    <TableHead>Potential Savings</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {negotiationOpportunities.map((opp) => (
                    <TableRow key={opp.id}>
                      <TableCell className="font-medium">{opp.supplier}</TableCell>
                      <TableCell>{opp.product}</TableCell>
                      <TableCell>${opp.currentPrice.toFixed(2)}</TableCell>
                      <TableCell>${opp.marketRate.toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge variant="destructive" className="flex items-center">
                          <TrendingUp className="h-3 w-3 mr-1" />
                          {opp.percentAbove}%
                        </Badge>
                      </TableCell>
                      <TableCell>${opp.potentialSavings.toFixed(2)}</TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm">Request Quote <ArrowRight className="h-4 w-4 ml-1" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="alerts" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Competitive Pricing Alerts</CardTitle>
              <CardDescription>
                Notifications when other restaurants receive better pricing from your suppliers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {pricingAlerts.map(alert => (
                  <PricingAlert key={alert.id} alert={alert} />
                ))}
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Product</TableHead>
                    <TableHead>Your Price</TableHead>
                    <TableHead>Competitor Price</TableHead>
                    <TableHead>Price Difference</TableHead>
                    <TableHead>Competitor</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pricingAlerts.map((alert) => (
                    <TableRow key={alert.id}>
                      <TableCell className="font-medium">{alert.supplier}</TableCell>
                      <TableCell>{alert.product}</TableCell>
                      <TableCell>${alert.yourPrice.toFixed(2)}</TableCell>
                      <TableCell>${alert.competitorPrice.toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge variant="destructive" className="flex items-center">
                          {alert.priceDiff}% higher
                        </Badge>
                      </TableCell>
                      <TableCell>{alert.competitor}</TableCell>
                      <TableCell>
                        {alert.actionTaken ? (
                          <Badge variant="outline" className="flex items-center bg-green-100 text-green-800 border-green-300">
                            <Check className="h-3 w-3 mr-1" />
                            Addressed
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="flex items-center bg-amber-100 text-amber-800 border-amber-300">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            Action needed
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="partnerships" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Strategic Partnership Recommendations</CardTitle>
              <CardDescription>
                Opportunities for volume discounts and improved relationships with key suppliers
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {partnershipRecommendations.map(partnership => (
                  <PartnershipRecommendation key={partnership.id} partnership={partnership} />
                ))}
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Current Spend</TableHead>
                    <TableHead>Tier Threshold</TableHead>
                    <TableHead>Additional Needed</TableHead>
                    <TableHead>Potential Savings</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {partnershipRecommendations.map((rec) => (
                    <TableRow key={rec.id}>
                      <TableCell className="font-medium">{rec.supplier}</TableCell>
                      <TableCell>{rec.category}</TableCell>
                      <TableCell>${rec.currentSpend.toFixed(2)}</TableCell>
                      <TableCell>${rec.requiredForTier.toFixed(2)}</TableCell>
                      <TableCell>${(rec.requiredForTier - rec.currentSpend).toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge variant="default" className="flex items-center bg-green-600">
                          <DollarSign className="h-3 w-3 mr-1" />
                          ${rec.potentialSavings.toFixed(2)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm">
                          <Handshake className="h-4 w-4 mr-1" />
                          Explore Partnership
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="switching" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Supplier Switching Analysis</CardTitle>
              <CardDescription>
                Calculate the actual cost impact of switching suppliers, including hidden costs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                {switchingOptions.map(option => (
                  <SwitchingCostCalculator key={option.id} option={option} />
                ))}
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Current Supplier</TableHead>
                    <TableHead>Potential Supplier</TableHead>
                    <TableHead>Annual Savings</TableHead>
                    <TableHead>Switching Costs</TableHead>
                    <TableHead>Break-even</TableHead>
                    <TableHead>Quality Risk</TableHead>
                    <TableHead>Recommendation</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {switchingOptions.map((option) => {
                    const annualSavings = (option.currentCost - option.newCost) * 12;
                    const breakEvenMonths = Math.ceil(option.switchingFees / (option.currentCost - option.newCost));
                    const isRecommended = 
                      annualSavings > option.switchingFees * 1.5 && 
                      option.qualityDifference !== "Lower" && 
                      option.reliabilityRisk !== "High";
                      
                    return (
                      <TableRow key={option.id}>
                        <TableCell className="font-medium">{option.currentSupplier}</TableCell>
                        <TableCell>{option.newSupplier}</TableCell>
                        <TableCell>
                          <Badge variant="default" className="flex items-center bg-green-600">
                            ${annualSavings.toFixed(2)}
                          </Badge>
                        </TableCell>
                        <TableCell>${option.switchingFees.toFixed(2)}</TableCell>
                        <TableCell>{breakEvenMonths} months</TableCell>
                        <TableCell>
                          <Badge variant={
                            option.qualityDifference === "Similar" ? "outline" : 
                            option.qualityDifference === "Slightly lower" ? "secondary" : 
                            "destructive"
                          }>
                            {option.reliabilityRisk}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {isRecommended ? (
                            <Badge variant="outline" className="flex items-center bg-green-100 text-green-800 border-green-300">
                              <Check className="h-3 w-3 mr-1" />
                              Recommended
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="flex items-center bg-red-100 text-red-800 border-red-300">
                              <X className="h-3 w-3 mr-1" />
                              Not Recommended
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SupplierRelationshipOptimizer;
